
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Button from 'sushiweb/Button';
import ZImage from 'sushiweb/ZImage';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import {
  addToCart,
  updateInCart,
  deleteFromCart,
} from '../../../redux/dispatchers/cartDispatcher';
import {
  toggleSimilarItemsModal,
  notifyOutOfStock,
} from '../../../redux/dispatchers/catalogDispatcher';
import {
  trackPopUp,
} from '../../../redux/dispatchers/userDispatcher';
import {
  debounce,
  getCookieForEntity,
  sendGTAGEvent,
  parseJson,
} from '../../../utils/helpers';
import CounterInput from '../../../components/shared/counterInput';
import styles from './ProductCardV1.module.scss';
import {
  CLIENT_TYPE_PETPOOJA,
  APP_MODE,
} from '../../../utils/constants/others';
import stateTypes from '../../../utils/constants/stateTypes';
import {
  ADD_TO_CART,
  ADD_TO_CART_PRODUCT_ID,
} from '../../../utils/constants/gtagEvents';
import ProductOffersModal from './ProductOffersModal';

const convertToSet = (arr) => {
  if (!arr) return new Set();
  if (arr instanceof Set) return arr;
  return new Set(Array.isArray(arr) ? arr : [arr]);
};

class ProductCardV1 extends React.Component {
  constructor(props) {
    super(props);

    this.debouncedSubmitHandler = debounce(this.submitHandler);

    this.state = {
      quantity: 0,
      prevQuantity: 0,
      maxAvailableQuantity: null,
      errorNudge: '',
      minOrderQty: 1,
      showOffersModal: false,
      currentTagIndex: 0,
      notified: false,
    };

    this.tagRotationInterval = null;
  }

  componentDidMount() {
    const { item } = this.props;
    if (item.Quantity) {
      this.setState({
        quantity: parseInt(item.Quantity.CartQuantity) || 0,
        prevQuantity: parseInt(item.Quantity.CartQuantity) || 0,
        minOrderQty: parseInt(item.Quantity.MinimumOrderQuantity) || 1,
      });
    }

    // Start tag rotation
    this.startTagRotation();
  }

  componentWillUnmount() {
    // Clear the tag rotation interval when component unmounts
    if (this.tagRotationInterval) {
      clearInterval(this.tagRotationInterval);
    }
  }

  startTagRotation = () => {
    const { item } = this.props;
    const tags = this.getProductTags(item);

    if (tags.length > 1) {
      // Use ViewFlipperTimeInMs from ViewFlipper data, fallback to 4000ms
      const rotationInterval = item?.ViewFlipper?.ViewFlipperTimeInMs || 4000;

      this.tagRotationInterval = setInterval(() => {
        this.setState((prevState) => ({
          currentTagIndex: (prevState.currentTagIndex + 1) % tags.length,
        }));
      }, rotationInterval);
    }
  };

  componentDidUpdate(prevProps) {
    const {
      item,
      addToCartStatus,
      updateInCartStatus,
      inProgressCart,
      cartAlternateProductSuggestion,
      cartErrorAvailableQty,
      addUpdateManyIssues,
      isIssuesModalOpen,
      dontOpenIssueModal,
    } = this.props;
    const prevItem = prevProps.item;

    if (prevItem?.Quantity?.CartQuantity !== item?.Quantity?.CartQuantity) {
      this.setState({
        quantity: parseInt(item.Quantity.CartQuantity) || 0,
        prevQuantity: parseInt(item.Quantity.CartQuantity) || 0,
      });
    }

    // Handle addToCart failure
    if (prevProps.addToCartStatus.state !== addToCartStatus.state) {
      if (addToCartStatus.state === stateTypes.STATE_FAIL) {
        if (
          convertToSet(prevProps.inProgressCart.add).has(item.Id) &&
          !convertToSet(inProgressCart.add).has(item.Id)
        ) {
          const addUpdateIssue =
            cartAlternateProductSuggestion &&
            cartAlternateProductSuggestion.SearchQuery &&
            !dontOpenIssueModal &&
            typeof addUpdateManyIssues === 'function';

          if (addUpdateIssue) {
            addUpdateManyIssues(
              [
                {
                  id: item.Id,
                  issueData: cartAlternateProductSuggestion,
                  issueProduct: item,
                  issueSelectedQuantity: parseInt(this.state.quantity) || 0,
                  issueAvailableQuantity: cartErrorAvailableQty,
                  issueNudgeMessage:
                    cartAlternateProductSuggestion.ModalNudgeMessage,
                },
              ],
              false,
              isIssuesModalOpen,
            );
          }

          // Check if rolling back to prevQuantity creates an inconsistent state
          const rolledBackQuantity = this.state.prevQuantity;
          const minOrderQtyInt = parseInt(this.state.minOrderQty) || 1;
          
          // If rolled back quantity is inconsistent with minOrderQty, reset to initial state
          if (rolledBackQuantity > 0 && rolledBackQuantity < minOrderQtyInt) {
            this.setState({
              quantity: 0,
              prevQuantity: 0,
              maxAvailableQuantity: null,
              errorNudge: '',
            });
          } else {
            this.setState(
              (prevState) => ({
                ...prevState,
                quantity: prevState.prevQuantity,
                maxAvailableQuantity: cartErrorAvailableQty,
                errorNudge:
                  cartAlternateProductSuggestion &&
                  cartAlternateProductSuggestion.ErrorNudge,
              }),
              () => {
                if (cartErrorAvailableQty) {
                  this.retryCartAddUpdate(cartErrorAvailableQty);
                }
              },
            );
          }
        }
      }
    }

    // Handle updateInCart failure
    if (prevProps.updateInCartStatus.state !== updateInCartStatus.state) {
      if (updateInCartStatus.state === stateTypes.STATE_FAIL) {
        if (
          convertToSet(prevProps.inProgressCart.update).has(item.Id) &&
          !convertToSet(inProgressCart.update).has(item.Id)
        ) {
          const addUpdateIssue =
            cartAlternateProductSuggestion &&
            cartAlternateProductSuggestion.SearchQuery &&
            !dontOpenIssueModal &&
            typeof addUpdateManyIssues === 'function';

          if (addUpdateIssue) {
            addUpdateManyIssues(
              [
                {
                  id: item.Id,
                  issueData: cartAlternateProductSuggestion,
                  issueProduct: item,
                  issueSelectedQuantity: parseInt(this.state.quantity) || 0,
                  issueAvailableQuantity: cartErrorAvailableQty,
                  issueNudgeMessage:
                    cartAlternateProductSuggestion.ModalNudgeMessage,
                },
              ],
              false,
              isIssuesModalOpen,
            );
          }

          // Check if rolling back to prevQuantity creates an inconsistent state
          const rolledBackQuantity = this.state.prevQuantity;
          const minOrderQtyInt = parseInt(this.state.minOrderQty) || 1;
          
          // If rolled back quantity is inconsistent with minOrderQty, reset to initial state
          if (rolledBackQuantity > 0 && rolledBackQuantity < minOrderQtyInt) {
            this.setState({
              quantity: 0,
              prevQuantity: 0,
              maxAvailableQuantity: null,
              errorNudge: '',
            });
          } else {
            this.setState(
              (prevState) => ({
                ...prevState,
                quantity: prevState.prevQuantity,
                maxAvailableQuantity: cartErrorAvailableQty,
                errorNudge:
                  cartAlternateProductSuggestion &&
                  cartAlternateProductSuggestion.ErrorNudge,
              }),
              () => {
                if (cartErrorAvailableQty) {
                  this.retryCartAddUpdate(cartErrorAvailableQty);
                }
              },
            );
          }
        }
      }
    }
  }

  retryCartAddUpdate = (retryQuantity) => {
    if (this.state.prevQuantity !== retryQuantity) {
      this.setState(
        (prevState) => ({
          ...prevState,
          quantity: retryQuantity,
        }),
        () => {
          this.submitHandler();
        },
      );
    }
  };

  submitHandler = (inputType) => {
    const {
      item,
      actions,
      cartUpdateSource,
      userData,
      location,
      referenceId,
      referenceType,
      getCartSummary,
      from,
      base64Query,
      type,
    } = this.props;
    const { quantity, prevQuantity, minOrderQty } = this.state;

    // Extract ReferenceId and ReferenceType from query params or props
    const query = queryString.parse(location?.search || '');
    const ReferenceId = referenceId || query.referenceId || '';
    const ReferenceType = referenceType || query.referenceType || '';

    const params = {
      ProductId: item.Id,
      OutletId: userData?.outlet?.id,
      ReferenceId,
      ReferenceType,
      InputType: inputType?.toString?.(),
      GetCartSummary: getCartSummary !== undefined ? getCartSummary : true,
      Source: cartUpdateSource,
      TrackingData: item.TrackingData,
    };

    const quantityInt = parseInt(quantity) || 0;
    const prevQuantityInt = parseInt(prevQuantity) || 0;
    const minOrderQtyInt = parseInt(minOrderQty) || 1;

    if (quantityInt === 0) {
      // delete from cart
      actions.deleteFromCart(
        {
          ...params,
          ProductIds: [params.ProductId],
        },
        true,
        from,
        base64Query,
        item?.CartParams,
      );
    } else if (quantityInt === minOrderQtyInt && prevQuantityInt === 0) {
      // add to cart
      sendGTAGEvent(ADD_TO_CART, { [ADD_TO_CART_PRODUCT_ID]: item.Id });
      actions.addToCart(
        {
          ...params,
          Quantity: minOrderQtyInt,
        },
        type,
        from,
        base64Query,
        item?.CartParams,
      );
    } else {
      // update in cart
      actions.updateInCart(
        {
          ...params,
          Quantity: quantityInt,
        },
        from,
        '',
        base64Query,
        item?.CartParams,
      );
    }

    this.setState({ prevQuantity: quantityInt });
  };

  handleOpenOffersModal = () => {
    this.setState({ showOffersModal: true });
  };

  handleCloseOffersModal = () => {
    this.setState({ showOffersModal: false });
  };

  handleModalAddToCart = (quantity, inputType) => {
    this.setState({ quantity }, () => {
      this.submitHandler(quantity, inputType);
    });
  };

  handleSignInClick = () => {
    const { history } = this.props;
    // Redirect to sign-in page
    if (history) {
      history.push('/auth/sign-in');
    } else {
      window.location.href = '/auth/sign-in';
    }
  };

  isOutOfStock = () => {
    const { item, withoutAuth, appMode } = this.props;

    if (appMode === APP_MODE.EXPRESS) {
      return item?.DisableAddToCart && !withoutAuth;
    }
    // Standard mode
    return !item?.IsInStock && !withoutAuth;
  };

  handleNotify = (e) => {
    e.stopPropagation();
    const {
      item,
      actions,
      cartUpdateSource,
      clearIssues,
      location,
      referenceId,
      referenceType,
    } = this.props;

    if (clearIssues) {
      clearIssues();
    }

    // Extract ReferenceId and ReferenceType from query params or props
    const query = queryString.parse(location?.search || '');
    const ReferenceId = referenceId || query.referenceId || '';
    const ReferenceType = referenceType || query.referenceType || '';

    actions.notifyOutOfStock(
      {
        ProductId: item.Id,
        Source: cartUpdateSource,
        ReferenceId,
        ReferenceType,
      },
      'ProductCardV1',
    );
    this.setState({ notified: true });
  };

  openSimilarItemsModal = (e) => {
    e.stopPropagation();
    const { item, addUpdateManyIssues, isIssuesModalOpen, actions } =
      this.props;
    const { SimilarItemsCTA } = item;

    if (!SimilarItemsCTA) return;

    const data = [
      {
        id: item.Id,
        issueProduct: item,
        issueData: {
          ErrorMessage: 'Out of stock',
          ErrorNudge: 'Out of stock',
          IssueHeading: 'Out of stock',
          IssueProduct: item,
          RailHeading: 'Similar products for you',
          SearchQuery: SimilarItemsCTA.Action?.ActionData?.SearchQuery,
        },
        issueAvailableQuantity: 0,
        issueSelectedQuantity: 0,
      },
    ];

    const { Action: { TrackingParams } = {} } = SimilarItemsCTA;
    if (TrackingParams) {
      actions.trackPopUp(TrackingParams);
    }

    if (addUpdateManyIssues) {
      addUpdateManyIssues(data, false, isIssuesModalOpen);
    }
  };

  quantityChangeHandler = (value, product, inputType) => {
    const newValue = typeof value === 'string' ? value.trim() : value;
    const isNumber = !isNaN(parseInt(newValue));

    if (newValue === '' || isNumber) {
      this.setState(
        (prevState) => ({
          ...prevState,
          quantity: value,
        }),
        () => {
          this.debouncedSubmitHandler(inputType);
        },
      );
    }
  };

  // enabling counter - sets quantity to minOrderQty and triggers add
  quantityAddHandler = () => {
    const { item } = this.props;
    const minOrderQty = parseInt(item?.Quantity?.MinimumOrderQuantity) || 1;

    this.setState(
      (prevState) => ({
        ...prevState,
        quantity: minOrderQty,
        prevQuantity: minOrderQty,
      }),
      () => {
        sendGTAGEvent(ADD_TO_CART, {
          [ADD_TO_CART_PRODUCT_ID]: item.Id,
        });
        this.submitHandler();
      },
    );
  };

  openProductDescription = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { item, cartUpdateSource } = this.props;
    if (item && cartUpdateSource) {
      window.location.href = item.Slug?.length
        ? `/in/${item.Slug}?source=${cartUpdateSource}`
        : `/buyer/product-detail/${item.Id}?source=${cartUpdateSource}`;
    }
  };

  /**
   * Extracts hex color from color object, preferring hexCode if available
   * @param {Object} colorObj - Color object with optional hexCode, tint and type properties
   * @returns {string|null} - Hex color string or null
   */
  getHexColor = (colorObj) => {
    if (!colorObj) {
      return null;
    }

    // Use hexCode directly if available
    if (colorObj.hexCode) {
      return colorObj.hexCode;
    }
  };

  /**
   * Parses ViewFlipper data and converts it to tag format
   * @param {Object} viewFlipper - ViewFlipper object from product
   * @returns {Array} - Array of tag objects
   */
  parseViewFlipperTags = (viewFlipper) => {
    if (!viewFlipper?.Views || !Array.isArray(viewFlipper.Views)) {
      return [];
    }

    return viewFlipper.Views.map((view) => {
      const tag = {};

      // Extract text from Heading
      if (view?.Heading?.text) {
        tag.text = view.Heading.text;
      }

      // Extract color from Heading - use hexCode directly if available
      if (view?.Heading?.color) {
        tag.color = this.getHexColor(view.Heading.color);
      }

      // Extract iconUrl from Image
      if (view?.Image?.url) {
        tag.iconUrl = view.Image.url;
      }

      // Extract bgColor and borderColor from UnderlineGradient
      if (
        view?.UnderlineGradient?.colors &&
        Array.isArray(view.UnderlineGradient.colors)
      ) {
        const gradientColors = view.UnderlineGradient.colors;

        // First color is typically bgColor, second is borderColor
        if (gradientColors[0]) {
          tag.bgColor = '#fff' || this.getHexColor(gradientColors[0]);
        }
        if (gradientColors[1]) {
          tag.borderColor = this.getHexColor(gradientColors[1]);
        }
      }

      return tag;
    }).filter((tag) => tag.text); // Only return tags that have text
  };

  getProductTags = (product) => {
    // Check for out of stock first
    if (this.isOutOfStock()) {
      return [
        {
          text: 'Out of Stock',
          color: '#9197A6',
          bgColor: '#F8F9FC',
          borderColor: '#E6E9EF',
        },
      ];
    }

    // Parse ViewFlipper data if available
    if (product?.ViewFlipper) {
      const viewFlipperTags = this.parseViewFlipperTags(product.ViewFlipper);
      if (viewFlipperTags.length > 0) {
        return viewFlipperTags;
      }
    }

    const tags = [];

    // Top Nudge Info tag
    if (product?.TopNudgeInfo?.Text) {
      tags.push(product.TopNudgeInfo.Text.replace(/<[^>]*>/g, ''));
    }

    // Ad tag
    if (product?.AdTextData) {
      tags.push('Ad');
    }

    // Dietary badge (veg/non-veg)
    if (product?.type_details?.image) {
      const typeText = product?.type_details?.text || 'Dietary';
      tags.push(typeText);
    }

    // Product Tag
    if (product?.Tag?.Text) {
      tags.push(product.Tag.Text);
    }

    // Offer V2 Tag
    if (product?.OfferV2?.Tag?.Text) {
      tags.push(product.OfferV2.Tag.Text.replace(/<[^>]*>/g, ''));
    }

    // SuperSaver Slab Tag
    if (product?.SuperSaverSlab?.Tag?.Text) {
      tags.push(product.SuperSaverSlab.Tag.Text);
    }

    // SuperSaver Slab Text
    if (product?.SuperSaverSlab?.Text) {
      tags.push(product.SuperSaverSlab.Text.replace(/<[^>]*>/g, ''));
    }

    // Discount Message
    if (product?.DiscountMessage) {
      tags.push(product.DiscountMessage);
    }

    // Offer Text (legacy)
    if (product?.Offer?.Text) {
      tags.push(product.Offer.Text);
    }

    // Delivery By V2
    if (product?.DeliveryByV2?.Text) {
      tags.push(product.DeliveryByV2.Text);
    }

    // Delivery By V3
    if (product?.DeliveryByV3?.Text) {
      tags.push(product.DeliveryByV3.Text.replace(/<[^>]*>/g, ''));
    }

    // Best Price
    if (product?.Price?.BestPrice) {
      tags.push(`Best price ${product.Price.BestPrice}`);
    }

    // Rating
    if (product?.Rating?.rating > 0) {
      tags.push(
        `Rating: ${product.Rating.rating}${product.Rating.ratings_count_text ? ` ${product.Rating.ratings_count_text}` : ''}`,
      );
    }

    // Offer Exclusion Text
    if (product?.OfferExclusionText) {
      tags.push(product.OfferExclusionText);
    }

    // Bottom Composite CTA
    if (product?.BottomCompositeCTA?.Text) {
      tags.push(product.BottomCompositeCTA.Text);
    }

    // Bottom CTA
    if (product?.BottomCTA?.Text) {
      tags.push(product.BottomCTA.Text);
    }

    // Offers array from OfferV2
    if (product?.OfferV2?.Offers && product.OfferV2.Offers.length > 0) {
      product.OfferV2.Offers.forEach((offer) => {
        if (offer?.Text) {
          tags.push(offer.Text.replace(/<[^>]*>/g, ''));
        }
      });
    }

    if (this.isOutOfStock()) {
      return [
        {
          text: 'Out of Stock',
          color: '#9197A6',
          bgColor: '#F8F9FC',
          borderColor: '#E6E9EF',
        },
      ];
    }

    return [];
  };

  renderRotatingTags = (product) => {
    const tags = this.getProductTags(product);
    const { currentTagIndex } = this.state;

    if (!tags || tags.length === 0) {
      return null;
    }

    const safeIndex = currentTagIndex % tags.length;
    const currentTag = tags[safeIndex];

    return (
      <div className={styles.rotatingTagWrapper}>
        <div className={styles.rotatingTag}>
          <svg
            className={styles.rotatingTagSvg}
            viewBox="0 0 111 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M111 0.5C108.791 0.5 107 2.29086 107 4.5V11.5C107 14.2614 104.761 16.5 102 16.5H9C6.23858 16.5 4 14.2614 4 11.5V4.5C4 2.29086 2.20914 0.5 0 0.5H111Z"
              fill={currentTag.bgColor || '#FFF7FA'}
            />
            <path
              d="M102 16.5V17V16.5ZM9 16.5V17V16.5ZM111 0.5V0C108.515 2.38419e-07 106.5 2.01472 106.5 4.5H107H107.5C107.5 2.567 109.067 1 111 1V0.5ZM107 4.5H106.5V11.5H107H107.5V4.5H107ZM107 11.5H106.5C106.5 13.9853 104.485 16 102 16V16.5V17C105.038 17 107.5 14.5376 107.5 11.5H107ZM102 16.5V16H9V16.5V17H102V16.5ZM9 16.5V16C6.51472 16 4.5 13.9853 4.5 11.5H4H3.5C3.5 14.5376 5.96243 17 9 17V16.5ZM4 11.5H4.5V4.5H4H3.5V11.5H4ZM4 4.5H4.5C4.5 2.01472 2.48528 2.38419e-07 4.80317e-08 0L0 0.5L-4.80317e-08 1C1.933 1 3.5 2.567 3.5 4.5H4ZM0 0.5V1H111V0.5V0H0V0.5Z"
              fill={currentTag.borderColor || '#FFD8E5'}
            />
          </svg>
          <span
            className={styles.rotatingTagText}
            style={{ color: currentTag.color }}
          >
            {currentTag.text}
          </span>
        </div>
      </div>
    );
  };

  renderTopBadge = (product) => {
    if (product?.TopNudgeInfo?.Text) {
      return (
        <div
          className={styles.topBadge}
          style={{
            backgroundColor: product.TopNudgeInfo.BackgroundColor,
            color: product.TopNudgeInfo.TextColor,
          }}
        >
          {product.TopNudgeInfo.Icon && (
            <img
              src={product.TopNudgeInfo.Icon}
              alt="badge"
              className={styles.badgeIcon}
            />
          )}
          <span
            dangerouslySetInnerHTML={{ __html: product.TopNudgeInfo.Text }}
          />
        </div>
      );
    }
    return null;
  };

  renderAdTag = (product) => {
    const shouldShowAdTag = product?.AdTextData;
    if (shouldShowAdTag) {
      return (
        <div className={styles.adTag}>
          <span>Ad</span>
        </div>
      );
    }
    return null;
  };

  renderDietaryBadge = (product) => {
    if (product?.type_details?.image) {
      const isOOS = this.isOutOfStock();
      return (
        <div className={styles.dietaryBadge}>
          <img
            src={product.type_details.image}
            alt="dietary badge"
            className={styles.dietaryIcon}
            style={isOOS ? { filter: 'grayscale(1)' } : {}}
          />
        </div>
      );
    }
    return null;
  };

  renderImage = (product) => {
    const isOOS = this.isOutOfStock();

    return (
      <div
        className={`${styles.imageContainer} ${isOOS ? styles.imageContainerOOS : ''}`}
        onClick={this.openProductDescription}
      >
        {this.renderRotatingTags(product)}
        <ZImage
          src={product.ImagePath}
          alt={product.Name}
          width="100%"
          height="100%"
          fit="contain"
          className={`${styles.productImage} ${isOOS ? styles.productImageOOS : ''}`}
        />
        <div
          className={styles.imageActionButton}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          {this.renderAddButton(product)}
        </div>
      </div>
    );
  };

  renderProductInfo = (product) => {
    const isOOS = this.isOutOfStock();

    return (
      <div
        className={`${styles.productInfo} ${isOOS ? styles.productInfoOOS : ''}`}
        onClick={this.openProductDescription}
      >
        <h3 className={styles.productName}>{product.Name}</h3>

        <div className={styles.metaInfo}>
          {product?.Quantity?.DisplayValue && (
            <span className={styles.weight}>
              {product.Quantity.DisplayValue}
            </span>
          )}

          {product?.Rating?.rating > 0 && (
            <>
              <span className={styles.separator}>|</span>
              <div className={styles.rating}>
                <img
                  className={styles.starIcon}
                  src={isOOS ? "https://b.zmtcdn.com/data/o2_assets/910b7683eb8cb8be5bbfdda4b811c90d1765946591.png" : "https://assets.hyperpure.com/data/images/starIcon/starIcon1759252824.png"}
                  alt="Star icon"
                  width="14"
                  height="14"
                />
                <span className={styles.ratingValue}>
                  {product.Rating.rating}&nbsp;
                  <span className="ml-px-4 text-gray-600">
                    {product?.Rating?.ratings_count_text}
                  </span>
                </span>
              </div>
            </>
          )}
        </div>

        {product?.NewSearchDeliveryTextV2?.text && (
          <div className={styles.deliveryInfo}>
            {product?.NewSearchDeliveryTextV2?.ImagePath && (
              <img
                src={product?.NewSearchDeliveryTextV2?.ImagePath}
                alt="delivery"
                className={styles.deliveryIcon}
              />
            )}
            <span>{product.NewSearchDeliveryTextV2.text}</span>
          </div>
        )}
      </div>
    );
  };

  renderPricing = (product) => {
    const isOOS = this.isOutOfStock();
    const { notified } = this.state;
    const { withoutAuth } = this.props;
    const isNotifyScheduled = product?.IsInStockNotifScheduled;

    return (
      <div className={styles.pricingSection}>
        <div className={styles.priceRow}>
          <span
            className={styles.price}
            style={{ color: product?.UnitPriceV2?.Color }}
          >
            {product?.UnitPriceV2?.Price}
          </span>
          {product?.UnitPriceV2?.CompareAtPrice && (
            <span className={styles.comparePrice}>
              {product.UnitPriceV2.CompareAtPrice}
            </span>
          )}
        </div>

        {isOOS ? (
          // Out of stock pricing section
          <div className={styles.oosActionRow}>
            {!notified && !isNotifyScheduled && !withoutAuth ? (
              <span className={styles.notifyMeLink} onClick={this.handleNotify}>
                Notify me
              </span>
            ) : (
              <span className={styles.notifiedText}>We'll notify you</span>
            )}
            {this.renderViewSimilarButton(product)}
          </div>
        ) : (
          // Normal pricing section
          <>
            <div
              style={{
                display: 'flex',
                verticalAlign: 'middle',
                flexWrap: 'wrap',
                gap: '4px',
                marginBottom: '8px',
              }}
            >
              {product?.OffersV3Tag?.Text &&
                <div className={styles.discountTag}>
                  <img
                    src="https://assets.hyperpure.com/data/images/images/DISCOUNT_TAG_WEB1765965176.png"
                    alt="Discount icon"
                    style={{ width: '14px', height: '14px' }}
                  />
                  <span className={styles.discountTagText}>{product?.OffersV3Tag?.Text}</span>
                </div>
              }

              {product?.OfferV2?.Tag && (
                <div className={styles.discountTag}>
                  <img
                    style={{ width: '14px', height: '14px' }}
                    src="https://assets.hyperpure.com/data/images/images/DISCOUNT_TAG_WEB1765965176.png"
                    alt="Discount icon"
                  />
                  <span className={styles.discountTagText}>
                    {product.OfferV2.Tag.Text ? product.OfferV2.Tag.Text : ''}
                  </span>
                </div>
              )}

              {product?.UnitPriceV2?.UnitPricePerUomWithPrePostConsidered && (
                <div className={styles.unitPrice}>
                  {product.UnitPriceV2?.UnitPricePerUomWithPrePostConsidered}
                </div>
              )}
            </div>

            {product?.Price?.BestRate?.text && (
              <span className={styles.bestPrice}>
                {product.Price.BestRate.text}
              </span>
            )}
          </>
        )}
      </div>
    );
  };

  renderViewSimilarButton = (product) => {
    const { SimilarItemsCTA } = product;

    if (!SimilarItemsCTA) return null;

    return (
      <button
        className={styles.viewSimilarButton}
        onClick={this.openSimilarItemsModal}
        style={{
          backgroundColor: SimilarItemsCTA.BgColor || '#fff',
          color: SimilarItemsCTA.TextColor || '#ef4f5f',
          borderColor: SimilarItemsCTA.BorderColor || '#ef4f5f',
        }}
      >
        {SimilarItemsCTA.Text || 'View similar'}
      </button>
    );
  };

  isCtaDisabled = (product) => {
    const { maxAvailableQuantity } = this.state;

    if (product?.DisableAddToCart || maxAvailableQuantity === 0) {
      return true;
    }

    if (product?.SegregatedInventoryView?.IsSellableInStock === false) {
      return true;
    }

    return false;
  };

  renderAddButton = (product) => {
    const { inProgressCart, isAuthenticated, actions, userData } = this.props;
    const { quantity, minOrderQty, maxAvailableQuantity, prevQuantity } =
      this.state;
    const isOOS = this.isOutOfStock();

    if (userData?.outlet?.ctaText === 'Launching Soon') {
      return (
        <div className={styles.addButtonContainer}>
          <div className={styles.launchingSoon}>Launching Soon</div>
        </div>
      );
    }

    // Show ADD button when prevQuantity is 0, or when add is in progress (to show loading state)
    const isAddInProgress = convertToSet(inProgressCart.add).has(product.Id);
    if ((prevQuantity === 0 || isAddInProgress) && maxAvailableQuantity !== 0) {
      return (
        <div className={styles.addButtonContainer}>
          <Button
            type="submit"
            loading={convertToSet(inProgressCart.add).has(product.Id)}
            size="small"
            btnColor="red"
            iconAlign="right"
            appearance="outline"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              // If there are offers, open the offers modal (for both authenticated and unauthenticated users)
              if (product?.OfferV2?.Offers?.length > 0) {
                this.handleOpenOffersModal();
                return;
              }
              // If user is not authenticated and no offers, redirect to sign-in
              if (!isAuthenticated) {
                this.handleSignInClick();
                return;
              }
              // Add to cart with minimum order quantity
              this.quantityAddHandler();
            }}
            className={`${styles.addButtonSushi} ${minOrderQty > 1 ? styles.addButtonWithMinQty : ''}`}
            disabled={this.isCtaDisabled(product) || this.isOutOfStock()}
          >
            <div className={styles.addButtonInner}>
              <div className={styles.addTextWrapper}>
                <span className={minOrderQty > 1 ? styles.addTextCompact : styles.addText}>ADD</span>
                <span className={styles.addIcon}>+</span>
              </div>
              {minOrderQty > 1 && (
                <span className={styles.minQtyInside}>
                  Min Qty {minOrderQty}
                </span>
              )}
            </div>
          </Button>
        </div>
      );
    }

    // Show counter when item is already in cart
    return (
      <div className={styles.addButtonContainer}>
        <div className={styles.counterWrapper}>
          <CounterInput
            type="text"
            placeholder={0}
            quantityChangeHandler={(val) =>
              this.quantityChangeHandler(val, product)
            }
            minLimit={0}
            maxLimit={
              maxAvailableQuantity === null ? undefined : maxAvailableQuantity
            }
            defaultVal={quantity}
            size="small"
            showLoader={
              convertToSet(inProgressCart.update).has(product.Id) ||
              convertToSet(inProgressCart.delete).has(product.Id)
            }
            minOrderQty={minOrderQty}
          />
        </div>
      </div>
    );
  };

  render() {
    const { item, withoutAuth, isAuthenticated, inProgressCart, location } = this.props;
    const { showOffersModal } = this.state;
    const product = item;

    if (!product) return null;

    // Check if current page is /buyer/cart
    const isCartPage = location?.pathname === '/buyer/cart';
    const cardClassName = isCartPage ? styles.cardCartPage : styles.card;

    return (
      <div className={styles.cardContainer}>
        <div className={cardClassName}>
          {this.renderAdTag(product)}

          <div className={styles.imageSection}>
            {this.renderDietaryBadge(product)}
            {this.renderImage(product)}
          </div>

          {this.renderProductInfo(product)}

          {this.renderPricing(product)}
        </div>

        {/* Product Offers Modal */}
        <ProductOffersModal
          visible={showOffersModal}
          onClose={this.handleCloseOffersModal}
          product={product}
          onAddToCart={this.handleModalAddToCart}
          isAuthenticated={isAuthenticated}
          onSignInClick={this.handleSignInClick}
          inProgressCart={inProgressCart}
        />
      </div>
    );
  }
}

export default withRouter(
  connect(
    (state) => ({
      userData: state.user.data,
      inProgressCart: state.cart.inProgress,
      cartAlternateProductSuggestion: state.cart.alternateProductSuggestion,
      cartErrorAvailableQty: state.cart.errorAvailableQty,
      isAuthenticated: state.auth.isAuthenticated,
      appMode: state.user?.config?.AppMode,

      // status
      addToCartStatus: state.status.cart_add,
      updateInCartStatus: state.status.cart_update,
      deleteFromCartStatus: state.status.cart_delete,
    }),
    (dispatch) => ({
      actions: bindActionCreators(
        {
          addToCart,
          updateInCart,
          deleteFromCart,
          toggleSimilarItemsModal,
          notifyOutOfStock,
          trackPopUp,
        },
        dispatch,
      ),
    }),
  )(ProductCardV1),
);
